import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { AuthContext } from '../../../auth/services/auth.context';
import { MatIconModule } from '@angular/material/icon';
import { ChangePasswordVo, UserProfileService } from '../user-profile.service';
import { environment } from '../../../../environments/environment';
import * as CryptoJS from 'crypto-js';
import { BannerAlertComponent } from '../../../components/banner-alert/banner-alert.component';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    CommonModule,
    MatCardModule,
    MatIconModule,
    BannerAlertComponent
  ],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {

  private snackBar = inject(MatSnackBar);

  changePasswordForm: FormGroup;
  userName: string;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideReenterPassword = true;
  passwordStrength = ''; // Track the strength of the new password
  passwordStrengthColor = ''; // Color code for password strength

  constructor(
    private fb: FormBuilder,
    private authContext: AuthContext,
    private userProfileService: UserProfileService,
    private router: Router,
  ) {
    this.userName = this.authContext.getUserInfo().userName.toLocaleLowerCase();
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          this.passwordComplexityValidator(),
          this.containsSubstringsFromUsernameValidator(this.userName),
          this.newPasswordNotSameAsCurrent()
        ]
      ],
      reenterPassword: ['', Validators.required],
    }, { validators: this.matchingPasswords('newPassword', 'reenterPassword') });
  }

  ngOnInit(): void { }

  // Validator to check password complexity
  passwordComplexityValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.value;
      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasDigit = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*]/.test(password);

      const valid = hasLowercase && hasUppercase && hasDigit && hasSpecialChar;
      this.checkPasswordStrength(password);
      return valid ? null : { passwordComplexity: true };
    };
  }

  // Validator to check if the new password contains substrings from the username
  containsSubstringsFromUsernameValidator(username: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.value;
      for (let i = 0; i < username.length - 2; i++) {
        const substring = username.substring(i, i + 3);
        if (password.includes(substring)) {
          return { containsUsernameSubstring: true };
        }
      }
      return null;
    };
  }

  // Validator to ensure new password is not the same as current password
  newPasswordNotSameAsCurrent(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control.parent;
      const currentPassword = formGroup?.get('currentPassword')?.value;
      const newPassword = control.value;

      return newPassword === currentPassword ? { passwordSameAsCurrent: true } : null;
    };
  }

  // Validator to check if new password and re-enter password match
  matchingPasswords(passwordKey: string, confirmPasswordKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const formGroup = group as FormGroup;
      const password = formGroup.get(passwordKey)?.value;
      const confirmPassword = formGroup.get(confirmPasswordKey)?.value;

      return password === confirmPassword ? null : { passwordsMismatch: true };
    };
  }

  // Function to check password strength
  checkPasswordStrength(password: string) {
    if (password.length < 8) {
      this.passwordStrength = 'Weak';
      this.passwordStrengthColor = 'red';
    } else if (password.length >= 8 && password.length <= 10 && /[A-Z]/.test(password) && /\d/.test(password)) {
      this.passwordStrength = 'Medium';
      this.passwordStrengthColor = 'orange';
    } else if (password.length >= 11 && /[A-Z]/.test(password) && /\d/.test(password) && /[!@#$%^&*]/.test(password)) {
      this.passwordStrength = 'Strong';
      this.passwordStrengthColor = 'green';
    } else {
      this.passwordStrength = 'Weak';
      this.passwordStrengthColor = 'red';
    }
  }

  // Handle submit
  onSubmit() {
    if (this.changePasswordForm.valid) {

      const formValues = this.changePasswordForm.value;
      const currentPassword = formValues.currentPassword;
      const newPassword = formValues.newPassword;

      const secret = environment.ivBase64Secret;
      const sha256Key = CryptoJS.SHA256(secret);
      const iv = CryptoJS.lib.WordArray.random(16);
      const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
      const encryptedCurrentPassword = CryptoJS.AES.encrypt(currentPassword, sha256Key, { iv: iv }).toString();
      const encryptedNewPassword = CryptoJS.AES.encrypt(newPassword, sha256Key, { iv: iv }).toString();
  
      // console.log('Password Changed:', formValues);
      const payload: ChangePasswordVo = {
        currentPassword: encryptedCurrentPassword,
        newPassword: encryptedNewPassword,
        instance: ivBase64
      };
      this.userProfileService.changeUserPassword(payload).subscribe({
        next: (response) => {
          if (response) {
            // console.log(response);
            this.openSnackBar("Password changed successfully", "Okay");
            localStorage.clear();
            this.router.navigate(['/app/login'], { replaceUrl: true });
          }
        },
        error: (error) => {
          console.error('POST error:', error);
        },
      });
    }
  }

  // Getters for form controls
  get currentPassword() {
    return this.changePasswordForm.get('currentPassword');
  }

  get newPassword() {
    return this.changePasswordForm.get('newPassword');
  }

  get reenterPassword() {
    return this.changePasswordForm.get('reenterPassword');
  }

  backToHome() {
    this.router.navigate(['/app/home'], { replaceUrl: true });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }
}
